/**
 * Hook to manage device status history for missed capture analysis
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import type { StatusHistoryDocument, StatusSnapshot } from "@/types";
import {
  getStatusHistory,
  subscribeToStatusHistory,
} from "@/services/viewService";

interface UseStatusHistoryParams {
  userId: string;
  projectId: string;
  viewId: string;
  enabled?: boolean;
}

interface UseStatusHistoryResult {
  todayHistory: StatusHistoryDocument | null;
  yesterdayHistory: StatusHistoryDocument | null;
  loading: boolean;
  error: Error | null;
  findNearestSnapshot: (timestamp: Date) => StatusSnapshot | undefined;
}

/**
 * Get date string in YYYY-MM-DD format
 */
function getDateString(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Hook for loading and subscribing to status history
 */
export function useStatusHistory({
  userId,
  projectId,
  viewId,
  enabled = true,
}: UseStatusHistoryParams): UseStatusHistoryResult {
  const [todayHistory, setTodayHistory] = useState<StatusHistoryDocument | null>(null);
  const [yesterdayHistory, setYesterdayHistory] = useState<StatusHistoryDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Calculate date strings
  const today = useMemo(() => {
    const now = new Date();
    return getDateString(now);
  }, []);

  const yesterday = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return getDateString(date);
  }, []);

  // Load yesterday's history (static, doesn't change)
  useEffect(() => {
    if (!enabled || !userId || !projectId || !viewId) {
      setLoading(false);
      return;
    }

    async function loadYesterdayHistory() {
      try {
        const history = await getStatusHistory(userId, projectId, viewId, yesterday);
        setYesterdayHistory(history);
      } catch (err) {
        console.error("Error loading yesterday status history:", err);
        // Don't set error for yesterday - it might just not exist
      }
    }

    loadYesterdayHistory();
  }, [userId, projectId, viewId, yesterday, enabled]);

  // Subscribe to today's history (real-time)
  useEffect(() => {
    if (!enabled || !userId || !projectId || !viewId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToStatusHistory(
      userId,
      projectId,
      viewId,
      today,
      (history) => {
        setTodayHistory(history);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, projectId, viewId, today, enabled]);

  // Combine all snapshots for searching
  const allSnapshots = useMemo(() => {
    const snapshots: StatusSnapshot[] = [];

    if (yesterdayHistory?.snapshots) {
      snapshots.push(...yesterdayHistory.snapshots);
    }

    if (todayHistory?.snapshots) {
      snapshots.push(...todayHistory.snapshots);
    }

    // Sort by timestamp ascending
    snapshots.sort((a, b) => {
      const aTime = a.timestamp?.toDate?.()?.getTime() || 0;
      const bTime = b.timestamp?.toDate?.()?.getTime() || 0;
      return aTime - bTime;
    });

    return snapshots;
  }, [todayHistory, yesterdayHistory]);

  /**
   * Find the status snapshot nearest to a given timestamp
   */
  const findNearestSnapshot = useCallback(
    (timestamp: Date): StatusSnapshot | undefined => {
      if (allSnapshots.length === 0) {
        return undefined;
      }

      const targetTime = timestamp.getTime();
      let nearest: StatusSnapshot | undefined;
      let smallestDiff = Infinity;

      for (const snapshot of allSnapshots) {
        const snapshotTime = snapshot.timestamp?.toDate?.()?.getTime() || 0;
        const diff = Math.abs(snapshotTime - targetTime);

        if (diff < smallestDiff) {
          smallestDiff = diff;
          nearest = snapshot;
        }
      }

      // Only return if within 30 minutes of target time
      const maxDiffMs = 30 * 60 * 1000;
      if (smallestDiff <= maxDiffMs) {
        return nearest;
      }

      return undefined;
    },
    [allSnapshots]
  );

  return {
    todayHistory,
    yesterdayHistory,
    loading,
    error,
    findNearestSnapshot,
  };
}
